import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SafeImage from '../../Utils/ImageHelper';
import { Helmet } from 'react-helmet-async';

function DashboardHeader({ company, companyLanguage, onLogout, bills, sales, recoveries, persons }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);

  // Detect if we are on the English dashboard route
  const isEnglishDashboard = location.pathname.startsWith('/company/dashboard-en');
  const dashboardBasePath = isEnglishDashboard ? '/company/dashboard-en' : '/company/dashboard';

  const isActive = (path) => {
    if (path === '/company/dashboard') {
      return location.pathname === '/company/dashboard' || location.pathname === '/company/dashboard-en';
    }
    return location.pathname.startsWith(path);
  };

  // Get page permissions from company
  const getPagePermission = (pageKey) => {
    if (!company?.pagePermissions) return true; // Default to enabled if no permissions set
    // Convert Map to object if needed, or use directly if already object
    const permissions = company.pagePermissions instanceof Map
      ? Object.fromEntries(company.pagePermissions)
      : company.pagePermissions;
    return permissions[pageKey] !== false; // Default to true if not explicitly false
  };

  // Dashboard tabs navigation links (same as DashboardTabsNavigation)
  const allNavLinks = [
    {
      type: 'tab',
      tab: 'overview',
      key: 'overview',
      labelEn: 'Overview',
      labelUr: 'نظارہ',
      onClick: () => navigate(`${dashboardBasePath}?tab=overview`)
    },
    {
      type: 'tab',
      tab: 'profile',
      key: 'profile',
      labelEn: 'Profile',
      labelUr: 'پروفائل',
      onClick: () => navigate(`${dashboardBasePath}?tab=profile`)
    },
    {
      type: 'tab',
      tab: 'persons',
      key: 'persons',
      labelEn: `Contact (${persons?.length || 0})`,
      labelUr: `رابطہ (${persons?.length || 0})`,
      onClick: () => navigate(`${dashboardBasePath}?tab=persons`)
    },
    {
      type: 'tab',
      tab: 'bills',
      key: 'bills',
      labelEn: `Bills (${bills?.length || 0})`,
      labelUr: `بلز (${bills?.length || 0})`,
      onClick: () => navigate(`${dashboardBasePath}?tab=bills`)
    },
    {
      type: 'tab',
      tab: 'sales',
      key: 'sales',
      labelEn: `Sales (${sales?.length || 0})`,
      labelUr: `سیلز (${sales?.length || 0})`,
      onClick: () => navigate(`${dashboardBasePath}?tab=sales`)
    },
    {
      type: 'tab',
      tab: 'recoveries',
      key: 'recoveries',
      labelEn: `Recoveries (${recoveries?.length || 0})`,
      labelUr: `ریکوریز (${recoveries?.length || 0})`,
      onClick: () => navigate(`${dashboardBasePath}?tab=recoveries`)
    },
    // Recovery Records link hidden - now accessible via RecoveriesTab button
    // { 
    //   type: 'page', 
    //   path: '/recovery/records', 
    //   key: 'recovery/records',
    //   labelEn: 'Recovery Records', 
    //   labelUr: 'ریکوری ریکارڈ',
    //   onClick: () => navigate('/recovery/records')
    // },
    {
      type: 'page',
      path: '/roz-namacha',
      key: 'roz-namacha',
      labelEn: 'Settled Bill',
      labelUr: 'بل ادائیگی',
      onClick: () => navigate('/roz-namacha')
    },
    {
      type: 'page',
      path: '/roznamacha',
      key: 'roznamacha',
      labelEn: 'Roz Namacha',
      labelUr: 'روزنامچہ',
      onClick: () => navigate('/roznamacha')
    },
    // General Sale link hidden - now accessible via SalesTab button
    // { 
    //   type: 'page', 
    //   path: '/general-sale', 
    //   key: 'general-sale',
    //   labelEn: 'General Sale', 
    //   labelUr: 'جنرل سیل',
    //   onClick: () => navigate('/general-sale')
    // },
    {
      type: 'page',
      path: '/expense-fields-details',
      key: 'expense-fields-details',
      labelEn: 'Expense Details',
      labelUr: 'اخراجات کی تفصیلات',
      onClick: () => navigate('/expense-fields-details')
    },
    {
      type: 'page',
      path: '/stock-report',
      key: 'stock-report',
      labelEn: 'Stock Report',
      labelUr: 'اسٹاک رپورٹ',
      onClick: () => navigate('/stock-report')
    },
    {
      type: 'page',
      path: '/user-manage',
      key: 'user-manage',
      labelEn: 'User Manage',
      labelUr: 'یوزر مینجمنٹ',
      onClick: () => navigate('/user-manage')
    },
    {
      type: 'tab',
      tab: 'receipts',
      key: 'receipts',
      labelEn: 'Receipt Management',
      labelUr: 'رسید مینجمنٹ',
      onClick: () => navigate(`${dashboardBasePath}?tab=receipts`)
    },
    {
      type: 'tab',
      tab: 'ledgers',
      key: 'ledgers',
      labelEn: 'Ledgers',
      labelUr: 'کھاتے',
      onClick: () => navigate(`${dashboardBasePath}?tab=ledgers`)
    },
  ];

  // Filter nav links based on page permissions
  const navLinks = allNavLinks.filter(link => {
    const pageKey = link.key || link.tab || link.path?.replace('/', '');
    return getPagePermission(pageKey);
  });

  return (
    <>
      <Helmet>
        <style>{`
          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes slideInRight {
            from {
              opacity: 0;
              transform: translateX(30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes pulse-glow {
            0%, 100% {
              box-shadow: 0 0 20px rgba(27, 94, 32, 0.3);
            }
            50% {
              box-shadow: 0 0 30px rgba(27, 94, 32, 0.5);
            }
          }
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-5px);
            }
          }
          .animate-fade-in-down {
            animation: fadeInDown 0.6s ease-out;
          }
          .animate-slide-in-left {
            animation: slideInLeft 0.6s ease-out;
          }
          .animate-slide-in-right {
            animation: slideInRight 0.6s ease-out;
          }
          .animate-pulse-glow {
            animation: pulse-glow 2s ease-in-out infinite;
          }
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </Helmet>
      <div className="bg-gradient-to-r from-white via-green-50/30 to-white shadow-xl border-b-2 border-green-200 sticky top-0 z-50 print:hidden backdrop-blur-sm" style={{ position: 'sticky', top: 0 }}>
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-5 overflow-hidden">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #059669 1px, transparent 0)`,
            backgroundSize: '30px 30px'
          }}></div>
        </div>

        {/* Floating Gradient Orbs */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-pulse" style={{ animationDelay: '1s' }}></div>

        <div className="relative max-w-7xl mx-auto px-4 py-2">
          {/* Top Row: Logo, Company Name, and Action Buttons */}
          <div className={`flex justify-between items-center mb-2 overflow-visible ${companyLanguage === 'ur' ? '' : ''}`}>
            {/* Company Name and Logo - Left side in Urdu, Right side in English */}
            <div className={`flex items-center gap-4 group overflow-visible ${companyLanguage === 'ur' ? 'animate-slide-in-left' : 'animate-slide-in-left'}`}>
              {company?.logo && (
                <div className="relative">
                  <div className="absolute inset-0 bg-green-400 rounded-xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-300 animate-pulse-glow"></div>
                  <SafeImage
                    src={company.logo}
                    alt="Logo"
                    className="relative h-12 w-12 md:h-14 md:w-14 rounded-xl object-cover border-2 border-green-300 shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 animate-float"
                    style={{ borderWidth: '2px' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className={`transform transition-transform duration-300 overflow-visible ${companyLanguage === 'ur' ? 'group-hover:-translate-x-1' : 'group-hover:translate-x-1'}`}>
                <h1 className={`text-xl font-bold bg-gradient-to-r from-green-700 to-emerald-700 bg-clip-text text-transparent ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", lineHeight: '1.5', paddingTop: '2px', paddingBottom: '2px', display: 'block', overflow: 'visible' } : { lineHeight: '1.5', paddingTop: '2px', paddingBottom: '2px', display: 'block', overflow: 'visible' }}>
                  {company?.shopName || company?.username || (companyLanguage === 'ur' ? 'کمپنی ڈیش بورڈ' : 'Company Dashboard')}
                </h1>
                <p className={`text-gray-600 text-xs font-medium mt-0 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'خوش آمدید' : 'Welcome'}, <span className="text-green-600 font-semibold">{company?.username}</span>
                </p>
              </div>
            </div>
            {/* Action Buttons - Right side in Urdu, Left side in English */}
            <div className={`flex items-center gap-3 ${companyLanguage === 'ur' ? 'animate-slide-in-right' : 'animate-slide-in-right'}`}>
              {/* Home Button - Hidden in Electron */}
              {!(window.isElectron || navigator.userAgent.includes('Electron')) && (
                <button
                  onClick={() => navigate('/')}
                  className={`group relative bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 hover:from-blue-600 hover:via-blue-700 hover:to-blue-800 text-white font-bold px-3 py-1.5 rounded-lg transition-all duration-300 flex items-center gap-1.5 transform hover:scale-105 hover:shadow-xl hover:shadow-blue-300 text-xs overflow-hidden ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                  style={{
                    boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)'
                  }}
                >
                  {/* Animated Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                      backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                      backgroundSize: '15px 15px'
                    }}></div>
                  </div>

                  {/* Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>

                  {/* Glow Effect */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-blue-600 rounded-2xl opacity-0 group-hover:opacity-50 blur-xl transition-opacity duration-300 -z-10"></div>

                  {/* Icon */}
                  <div className="relative z-10 bg-white/20 backdrop-blur-sm rounded-lg p-1.5 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                    </svg>
                  </div>

                  {/* Text */}
                  <span className="relative z-10 font-semibold">{companyLanguage === 'ur' ? 'ہوم' : 'Home'}</span>
                </button>
              )}

              {/* Logout Button */}
              <button
                onClick={onLogout}
                className={`group relative bg-gradient-to-br from-red-500 via-red-600 to-red-700 hover:from-red-600 hover:via-red-700 hover:to-red-800 text-white font-bold px-3 py-1.5 rounded-lg transition-all duration-300 flex items-center gap-1.5 transform hover:scale-105 hover:shadow-xl hover:shadow-red-300 text-xs overflow-hidden ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                style={{
                  boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)'
                }}
              >
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                    backgroundSize: '15px 15px'
                  }}></div>
                </div>

                {/* Shine Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>

                {/* Glow Effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-red-400 to-red-600 rounded-2xl opacity-0 group-hover:opacity-50 blur-xl transition-opacity duration-300 -z-10"></div>

                {/* Icon */}
                <div className="relative z-10 bg-white/20 backdrop-blur-sm rounded-md p-0.5 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </div>

                {/* Text */}
                <span className="relative z-10 font-semibold">{companyLanguage === 'ur' ? 'لاگ آؤٹ' : 'Logout'}</span>
              </button>
            </div>
          </div>

          {/* Navigation Links - 2 Rows on Mobile, Single Row on Desktop */}
          <div className="grid grid-cols-2 md:flex md:items-center gap-2 md:overflow-x-auto pb-1 scrollbar-hide" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
            {navLinks.map((link, index) => {
              let isLinkActive = false;
              if (link.type === 'page') {
                isLinkActive = isActive(link.path);
              } else if (link.type === 'tab') {
                // For dashboard tabs, check if we're on dashboard and tab matches
                const currentTab = new URLSearchParams(location.search).get('tab') || 'overview';
                isLinkActive = (location.pathname === '/company/dashboard' || location.pathname === '/company/dashboard-en') && currentTab === link.tab;
              }

              return (
                <button
                  key={link.path || link.tab || index}
                  onClick={link.onClick}
                  className={`group relative px-3 py-1.5 rounded-lg font-semibold text-xs transition-all duration-300 whitespace-nowrap flex items-center gap-1.5 transform hover:scale-105 overflow-hidden ${isLinkActive
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-200'
                    : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700 border-2 border-transparent hover:border-green-200'
                    } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                  style={{
                    animation: `fadeInDown 0.5s ease-out ${index * 0.05}s both`
                  }}
                >
                  {/* Active Indicator */}
                  {isLinkActive && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 rounded-full"></div>
                  )}

                  {/* Hover Shine Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700 ${isLinkActive ? 'opacity-50' : 'opacity-0'}`}></div>

                  <span className="relative z-10 flex items-center gap-1.5">
                    {companyLanguage === 'ur' ? link.labelUr : link.labelEn}
                    {isLinkActive && (
                      <svg className="w-3.5 h-3.5 transform group-hover:rotate-12 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

export default DashboardHeader;


